import React, { useState, useEffect, useMemo, useContext, createContext, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Play, CreditCard, Code, ImageIcon, MapPin, ChevronDown,
  ExternalLink, Clock, Lock, AlertTriangle, Globe,
  // P2 catalog parity — carousel / music / product icons
  ChevronLeft, ChevronRight, Music, ShoppingBag,
  // P3-2 tip jar
  Coffee, Heart,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';
import { pagesAPI } from '../../services/api';
import {
  loadLocale, t, loc, RTL_LOCALES, SUPPORTED_LOCALES,
  type PublicLocaleStrings,
} from '../../locales/public/index';
import { TurnstileWidget } from './TurnstileWidget';

/* ─── Types ─── */

/**
 * P3-1: per-block scheduled visibility. Mirrors the Python shape in
 * backend/app/models/page.py — every field is optional; absence of a
 * field means "no restriction on that axis". Parsed/evaluated client-
 * side by `isScheduleActive` below (defense-in-depth with the backend
 * filter in pages.py::get_public_page).
 */
interface VisibilitySchedule {
  start_at?: string | null;
  end_at?: string | null;
  days?: number[] | null;        // 0=Mon .. 6=Sun (ISO weekday)
  start_time?: string | null;    // "HH:MM" local
  end_time?: string | null;      // "HH:MM" local
  timezone?: string | null;      // IANA tz name
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface Block { id: string; block_type: string; settings: Record<string, any>; order: number; is_visible?: boolean; visibility_schedule?: VisibilitySchedule | null; }
// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface PublicPageData { title: string; slug: string; description?: string; blocks: Block[]; theme: Record<string, any>; favicon_url?: string; og_image_url?: string; seo_title?: string; seo_description?: string; no_index?: boolean; google_analytics_id?: string; facebook_pixel_id?: string; password_protected?: boolean; sensitive_content?: boolean; default_locale?: string; locale?: string; available_locales?: string[]; is_preview?: boolean; }

interface LocaleCtxVal { locale: string; defaultLocale: string; strings: PublicLocaleStrings | null; availableLocales: string[]; }
const LocaleCtx = createContext<LocaleCtxVal>({ locale: 'en', defaultLocale: 'en', strings: null, availableLocales: [] });

type PublicEventType = 'page_view' | 'block_click' | 'form_submit' | 'payment_start' | 'tip_start';

type PublicPageEventPayload = {
  event_type: PublicEventType;
  block_id?: string;
  block_type?: string;
  link_url?: string;
  link_label?: string;
  metadata?: Record<string, unknown>;
};

const PageTrackCtx = createContext<(payload: PublicPageEventPayload) => void>(() => {});

const publicFallbackImage = (from: string, to: string, accent: string) => (
  `data:image/svg+xml;utf8,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 420">
      <defs>
        <linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stop-color="${from}"/>
          <stop offset="1" stop-color="${to}"/>
        </linearGradient>
      </defs>
      <rect width="640" height="420" rx="42" fill="url(#g)"/>
      <circle cx="520" cy="72" r="92" fill="${accent}" opacity=".34"/>
      <circle cx="92" cy="344" r="118" fill="#ffffff" opacity=".2"/>
      <path d="M70 310 C170 210 230 265 310 180 C382 104 455 150 570 60 L570 420 L70 420 Z" fill="#ffffff" opacity=".42"/>
      <path d="M95 330 C205 235 265 285 342 205 C405 140 468 166 548 106" fill="none" stroke="#ffffff" stroke-width="18" stroke-linecap="round" opacity=".58"/>
    </svg>
  `)}`
);

const PUBLIC_MEDIA_FALLBACKS = [
  publicFallbackImage('#d1fae5', '#a7f3d0', '#059669'),
  publicFallbackImage('#e0f2fe', '#bae6fd', '#0284c7'),
  publicFallbackImage('#fef3c7', '#fed7aa', '#f97316'),
] as const;

const getPublicFallbackImage = (index = 0) =>
  PUBLIC_MEDIA_FALLBACKS[index % PUBLIC_MEDIA_FALLBACKS.length];

const getOrCreatePublicId = (key: string, storage: Storage = window.localStorage): string => {
  const fallback = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  try {
    const existing = storage.getItem(key);
    if (existing) { return existing; }
    storage.setItem(key, fallback);
  } catch {
    return fallback;
  }
  return fallback;
};

const publicPageUtm = (params: URLSearchParams): Record<string, string> => {
  const out: Record<string, string> = {};
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid'].forEach((key) => {
    const value = params.get(key);
    if (value) { out[key] = value.slice(0, 200); }
  });
  return out;
};

const sendPublicPageEvent = (
  slug: string,
  params: URLSearchParams,
  payload: PublicPageEventPayload,
) => {
  const visitorId = getOrCreatePublicId('plugd_public_visitor_id');
  const sessionId = getOrCreatePublicId(`plugd_public_session_${slug}`, window.sessionStorage);
  const body = JSON.stringify({
    ...payload,
    visitor_id: visitorId,
    session_id: sessionId,
    utm: publicPageUtm(params),
  });
  const url = `/api/p/${encodeURIComponent(slug)}/events`;
  const blob = new Blob([body], { type: 'application/json' });
  if (navigator.sendBeacon?.(url, blob)) {
    return;
  }
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => {
    // Analytics must never interrupt the visitor experience.
  });
};

type PublicPageQueryError = {
  response?: {
    status?: number;
    data?: {
      code?: string;
    };
  };
};

const getPublicPageResponse = (error: unknown): PublicPageQueryError['response'] => {
  return (error as PublicPageQueryError | null)?.response;
};

const isTerminalPublicPageError = (error: unknown): boolean => {
  const response = getPublicPageResponse(error);
  const status = response?.status;
  return (
    status === 410 ||
    status === 404 ||
    status === 401 ||
    status === 403 ||
    response?.data?.code === 'page_unpublished'
  );
};

const PM: Record<string, { color: string; icon: string; label: string }> = {
  facebook: { color: '#1877F2', icon: 'f', label: 'Facebook' }, instagram: { color: '#E4405F', icon: '📷', label: 'Instagram' },
  twitter: { color: '#1DA1F2', icon: '𝕏', label: 'X' }, x: { color: '#000', icon: '𝕏', label: 'X' },
  youtube: { color: '#FF0000', icon: '▶', label: 'YouTube' }, tiktok: { color: '#000', icon: '♪', label: 'TikTok' },
  linkedin: { color: '#0A66C2', icon: 'in', label: 'LinkedIn' }, pinterest: { color: '#E60023', icon: 'P', label: 'Pinterest' },
  telegram: { color: '#26A5E4', icon: '✈', label: 'Telegram' }, whatsapp: { color: '#25D366', icon: '✆', label: 'WhatsApp' },
  snapchat: { color: '#FFFC00', icon: '👻', label: 'Snapchat' }, twitch: { color: '#9146FF', icon: '⏵', label: 'Twitch' },
  threads: { color: '#000', icon: '@', label: 'Threads' }, bluesky: { color: '#0085FF', icon: '🦋', label: 'Bluesky' },
  tumblr: { color: '#36465D', icon: 'T', label: 'Tumblr' }, signal: { color: '#3A76F0', icon: 'S', label: 'Signal' },
};

/* ─── Font helpers (P1.5) ─── */

/**
 * Wrap a bare Google Font family name into a CSS font-family stack. If
 * the value already looks like a CSS stack (contains a comma or a
 * quote) it's returned unchanged so we don't double-wrap legacy themes.
 */
const wrapFontFamily = (family: string | undefined | null): string => {
  if (!family) { return "-apple-system, BlinkMacSystemFont, sans-serif"; }
  if (family.includes(',') || family.includes("'") || family.includes('"')) {
    return family;
  }
  return `'${family}', -apple-system, BlinkMacSystemFont, sans-serif`;
};

/**
 * Only bare font names (e.g. `Playfair Display`) can be loaded from
 * Google Fonts — raw CSS stacks and empty strings are rejected.
 */
const isGoogleFontName = (family: string | undefined | null): family is string => {
  if (!family) { return false; }
  const trimmed = family.trim();
  if (trimmed.length === 0) { return false; }
  if (trimmed.includes(',') || trimmed.includes("'") || trimmed.includes('"')) {
    return false;
  }
  return true;
};

/* ─── Visibility schedule (P3-1) ─── */

/**
 * Parse a value that might be an ISO-8601 datetime string. Accepts both
 * "Z" suffix and explicit offsets. Returns `null` on any failure so the
 * caller can fail open (malformed schedules stay visible).
 */
const _parseScheduleISO = (value: unknown): Date | null => {
  if (typeof value !== 'string' || value.length === 0) { return null; }
  // `new Date` accepts both "…Z" and "…+00:00" natively; no replacement
  // needed. We still guard against NaN.
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) { return null; }
  return d;
};

/**
 * Parse "HH:MM" into [hours, minutes] tuple, or null on failure.
 */
const _parseHHMM = (value: unknown): [number, number] | null => {
  if (typeof value !== 'string') { return null; }
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) { return null; }
  const h = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  if (Number.isNaN(h) || Number.isNaN(mm)) { return null; }
  if (h < 0 || h > 23 || mm < 0 || mm > 59) { return null; }
  return [h, mm];
};

/**
 * Get the (hour, minute, iso-weekday 0=Mon..6=Sun) for `now` as observed
 * in the given IANA timezone. Uses Intl.DateTimeFormat which is in every
 * modern browser. Falls back to local time if the tz is invalid.
 */
const _localPartsIn = (
  now: Date,
  tz: string | null | undefined,
): { hour: number; minute: number; weekday: number } => {
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz || 'UTC',
      hour: '2-digit',
      minute: '2-digit',
      weekday: 'short',
      hour12: false,
    });
    const parts = fmt.formatToParts(now);
    let hour = 0;
    let minute = 0;
    let weekdayStr = 'Mon';
    for (const p of parts) {
      if (p.type === 'hour') { hour = parseInt(p.value, 10) % 24; }
      else if (p.type === 'minute') { minute = parseInt(p.value, 10); }
      else if (p.type === 'weekday') { weekdayStr = p.value; }
    }
    const dayMap: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
    const weekday = dayMap[weekdayStr] ?? 0;
    return { hour, minute, weekday };
  } catch {
    // Invalid tz — fall back to the visitor's local time.
    const weekdayJS = now.getDay(); // 0=Sun..6=Sat
    const weekday = (weekdayJS + 6) % 7; // 0=Mon..6=Sun
    return { hour: now.getHours(), minute: now.getMinutes(), weekday };
  }
};

/**
 * P3-1: evaluate whether a block's visibility schedule currently allows
 * the block to be shown. Mirrors `is_schedule_active` in
 * backend/app/services/builder/visibility.py.
 *
 * Design rules:
 *   • Fail OPEN — any malformed field is ignored so broken schedules
 *     never hide content forever.
 *   • Absence of a field = "no restriction on that axis".
 *   • Overnight windows: if start_time > end_time the window wraps past
 *     midnight (e.g. 22:00 → 06:00).
 */
const isScheduleActive = (
  schedule: VisibilitySchedule | null | undefined,
  nowParam?: Date,
): boolean => {
  if (!schedule || typeof schedule !== 'object') { return true; }
  const now = nowParam || new Date();

  // ── Date bounds (UTC ISO) ──
  const start = _parseScheduleISO(schedule.start_at);
  if (start && now.getTime() < start.getTime()) { return false; }
  const end = _parseScheduleISO(schedule.end_at);
  if (end && now.getTime() >= end.getTime()) { return false; }

  const tz = typeof schedule.timezone === 'string' && schedule.timezone.length > 0
    ? schedule.timezone
    : null;

  // ── Days of week (ISO 0=Mon..6=Sun in tz) ──
  const days = Array.isArray(schedule.days) ? schedule.days : null;
  if (days && days.length > 0) {
    const { weekday } = _localPartsIn(now, tz);
    const allowed = days.filter(
      (d) => typeof d === 'number' && Number.isInteger(d) && d >= 0 && d <= 6,
    );
    if (allowed.length > 0 && !allowed.includes(weekday)) { return false; }
  }

  // ── Time-of-day window (local HH:MM in tz) ──
  const startHM = _parseHHMM(schedule.start_time);
  const endHM = _parseHHMM(schedule.end_time);
  if (startHM || endHM) {
    const { hour, minute } = _localPartsIn(now, tz);
    const cur = hour * 60 + minute;
    if (startHM && endHM) {
      const s = startHM[0] * 60 + startHM[1];
      const e = endHM[0] * 60 + endHM[1];
      if (s <= e) {
        if (cur < s || cur >= e) { return false; }
      } else {
        // Overnight wrap: valid if cur >= s OR cur < e
        if (cur < s && cur >= e) { return false; }
      }
    } else if (startHM) {
      const s = startHM[0] * 60 + startHM[1];
      if (cur < s) { return false; }
    } else if (endHM) {
      const e = endHM[0] * 60 + endHM[1];
      if (cur >= e) { return false; }
    }
  }

  return true;
};

/* ═══ Block Components ═══ */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const AvatarBlock: React.FC<{ s: Record<string, any>; pc: string }> = ({ s, pc }) => {
  const { locale, defaultLocale } = useContext(LocaleCtx);
  const sizes: Record<string, number> = { sm: 80, md: 110, lg: 140, xl: 170 };
  const sz = sizes[s.size || 'md'] || 110;
  const name = loc(s, 'name', locale, defaultLocale);
  const description = loc(s, 'description', locale, defaultLocale);

  // P1.3: three-layer avatar fallback chain emitted by the backend for
  // every avatar block — primary image → dicebear SVG → inline initials
  // SVG. If every layer fails we degrade to a gradient + initials tile.
  const primary = typeof s.image_url === 'string' ? s.image_url : '';
  const fallback = typeof s.fallback_image_url === 'string' ? s.fallback_image_url : '';
  const offline = typeof s.offline_image_url === 'string' ? s.offline_image_url : '';
  const avatarChain = useMemo(
    () => [primary, fallback, offline].filter((url) => url.length > 0),
    [primary, fallback, offline],
  );
  const [chainIndex, setChainIndex] = useState(0);
  // Reset when the underlying URLs change (e.g. editor swap, block reload).
  useEffect(() => {
    setChainIndex(0);
  }, [avatarChain]);
  const currentSrc = chainIndex < avatarChain.length ? avatarChain[chainIndex] : '';
  const showImage = currentSrc.length > 0;
  const handleAvatarError = () => {
    // Advance past the failed layer; the next render either picks the
    // next URL or, once we fall off the end, switches to the gradient
    // initials tile below.
    setChainIndex((idx) => idx + 1);
  };

  return (
    <div className="flex flex-col items-center gap-3 pb-2">
      {showImage ? (
        <div style={{ width: sz, height: sz, padding: 4, background: `linear-gradient(135deg, ${pc}, ${pc}66)`, borderRadius: '50%' }}>
          <div style={{ width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden' }}>
            <img
              key={currentSrc}
              src={currentSrc}
              alt={name || ''}
              loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              onError={handleAvatarError}
            />
          </div>
        </div>
      ) : (
        <div
          style={{ width: sz, height: sz, background: `linear-gradient(135deg, ${pc}, ${pc}99)` }}
          className="rounded-full flex items-center justify-center text-white font-bold shadow-xl"
        >
          <span style={{ fontSize: sz * 0.38 }}>{(name || 'P').charAt(0).toUpperCase()}</span>
        </div>
      )}
      {s.show_name !== false && name && (
        <h1 className="text-2xl font-extrabold tracking-tight">{name}</h1>
      )}
      {description && (
        <p className="text-sm opacity-50 text-center max-w-[280px] leading-relaxed">{description}</p>
      )}
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const TextBlock: React.FC<{ s: Record<string, any> }> = ({ s }) => {
  const { locale, defaultLocale } = useContext(LocaleCtx);
  const fsMap: Record<string, string> = {
    small: '0.85rem',
    medium: '0.95rem',
    large: '1.1rem',
    heading: '1.6rem',
    subtitle: '1rem',
    body: '0.95rem',
  };
  const content = loc(s, 'content', locale, defaultLocale) || loc(s, 'text', locale, defaultLocale);
  if (!content.replace(/<[^>]*>/g, '').trim()) return null;
  const isHtml = content.includes('<') && content.includes('>');
  const alignment = ['left', 'center', 'right'].includes(s.alignment) ? s.alignment : 'center';
  const variant = typeof s.variant === 'string' ? s.variant : '';
  const fontSize = fsMap[variant] || fsMap[s.font_size || 'medium'] || fsMap.medium;
  const className =
    variant === 'heading'
      ? 'font-extrabold tracking-tight opacity-100 leading-tight'
      : variant === 'subtitle'
        ? 'font-medium opacity-60 leading-relaxed'
        : 'opacity-70 leading-relaxed';
  return (
    <div
      style={{
        fontSize,
        paddingTop: s.top_indent || 0,
        paddingBottom: s.bottom_indent || 0,
        textAlign: alignment as React.CSSProperties['textAlign'],
      }}
      className={className}
    >
      {isHtml ? <div dangerouslySetInnerHTML={{ __html: content }} className="[&_strong]:font-bold [&_em]:italic [&_a]:underline" /> : content}
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const HeadingBlock: React.FC<{ s: Record<string, any> }> = ({ s }) => {
  const { locale, defaultLocale } = useContext(LocaleCtx);
  const content =
    loc(s, 'content', locale, defaultLocale) ||
    loc(s, 'text', locale, defaultLocale) ||
    loc(s, 'title', locale, defaultLocale);
  if (!content.replace(/<[^>]*>/g, '').trim()) return null;
  const isHtml = content.includes('<') && content.includes('>');
  const alignment = ['left', 'center', 'right'].includes(s.alignment) ? s.alignment : 'center';
  return (
    <div
      className="py-1 font-extrabold tracking-tight leading-tight"
      style={{ textAlign: alignment as React.CSSProperties['textAlign'] }}
    >
      {isHtml ? (
        <div
          className="text-2xl [&_h1]:text-3xl [&_h2]:text-2xl [&_strong]:font-extrabold"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      ) : (
        <h2 className="text-2xl">{content}</h2>
      )}
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ButtonBlock: React.FC<{ s: Record<string, any>; pc: string; r: string }> = ({ s, pc, r }) => {
  const { locale, defaultLocale } = useContext(LocaleCtx);
  const szMap: Record<string, string> = { small: 'py-3.5 text-sm', medium: 'py-4 text-[15px]', large: 'py-5 text-base' };
  const bg = s.bg_color || pc;
  const title = loc(s, 'title', locale, defaultLocale) || loc(s, 'label', locale, defaultLocale) || 'Button';
  const subtitle = loc(s, 'subtitle', locale, defaultLocale);
  return (
    <div className="py-1.5">
      <a
        href={s.url || '#'} target="_blank" rel="noopener noreferrer"
        className={`block w-full text-center ${szMap[s.button_size || 'medium']} text-white font-semibold shadow-lg transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 ${s.animation === 'pulse' ? 'animate-pulse' : s.animation === 'bounce' ? 'animate-bounce' : ''}`}
        style={{ backgroundColor: bg, borderRadius: r, boxShadow: `0 4px 14px ${bg}40` }}
      >
        <span className="flex items-center justify-center gap-2">
          {s.icon && <span>{s.icon}</span>}
          {title}
          <ExternalLink className="w-3.5 h-3.5 opacity-40" />
        </span>
        {subtitle && <div className="text-xs opacity-60 mt-0.5">{subtitle}</div>}
      </a>
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const IconTextBlock: React.FC<{ s: Record<string, any> }> = ({ s }) => {
  const { locale, defaultLocale } = useContext(LocaleCtx);
  const items: Array<{ icon?: string; label?: string; title?: string; text?: string; description?: string; url?: string }> =
    Array.isArray(s.items) ? s.items : [];
  if (items.length > 0) {
    return (
      <div className="py-2 space-y-2">
        {s.title && (
          <p className="text-xs font-bold uppercase tracking-widest opacity-40 text-center">
            {s.title}
          </p>
        )}
        {items.map((item, index) => (
          <IconTextBlock
            key={`${item.label || item.title || item.text || 'item'}-${index}`}
            s={{
              ...item,
              text: item.text || item.label || item.title,
              icon: item.icon || s.icon,
              icon_bg: s.icon_bg,
              icon_color: s.icon_color,
            }}
          />
        ))}
      </div>
    );
  }
  const text = loc(s, 'text', locale, defaultLocale);
  const description = loc(s, 'description', locale, defaultLocale);
  const Wrapper = s.url ? 'a' : 'div';
  const wrapperProps = s.url ? { href: s.url, target: '_blank', rel: 'noopener noreferrer' } : {};
  return (
    <Wrapper
      className={`flex items-start gap-3 p-4 rounded-xl transition-all duration-200 ${s.url ? 'hover:shadow-md hover:scale-102 cursor-pointer' : ''}`}
      style={s.url ? { textDecoration: 'none' } : {}}
      {...wrapperProps}
    >
      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: s.icon_bg || '#eff6ff' }}>
        <span style={{ color: s.icon_color || '#3b82f6' }} className="text-lg">{s.icon || '⭐'}</span>
      </div>
      <div className="flex-1">
        <p className="text-sm font-semibold text-gray-900">{text || 'Title'}</p>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
    </Wrapper>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SocialBlock: React.FC<{ s: Record<string, any> }> = ({ s }) => {
  const links: { platform: string; url: string }[] = Array.isArray(s.links) && s.links.length > 0
    ? s.links
    : Array.isArray(s.networks)
      ? s.networks.map((platform: string) => ({ platform, url: '' }))
      : [];
  if (!links.length) return null;
  return (
    <div className="flex justify-center gap-3 py-3 flex-wrap">
      {links.map((link, i) => {
        const m = PM[link.platform.toLowerCase()];
        return (
          <a key={i} href={link.url || '#'} target="_blank" rel="noopener noreferrer"
            className="w-12 h-12 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-lg transition-all duration-200 hover:scale-110 hover:-translate-y-1 hover:shadow-xl"
            style={{ backgroundColor: m?.color || '#6b7280', boxShadow: `0 4px 12px ${m?.color || '#6b7280'}50` }}
            title={m?.label || link.platform}
          >
            {m?.icon || link.platform.charAt(0).toUpperCase()}
          </a>
        );
      })}
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MessengersBlock: React.FC<{ s: Record<string, any> }> = ({ s }) => {
  const items: { platform: string; value: string }[] = s.items || [];
  if (!items.length) return null;
  return (
    <div className="flex justify-center gap-3 py-3 flex-wrap">
      {items.map((item, i) => {
        const m = PM[item.platform.toLowerCase()];
        return (
          <div key={i} className="w-12 h-12 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-lg"
            style={{ backgroundColor: m?.color || '#6b7280' }} title={m?.label || item.platform}
          >{m?.icon || item.platform.charAt(0).toUpperCase()}</div>
        );
      })}
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DividerBlock: React.FC<{ s: Record<string, any> }> = ({ s }) => {
  const c = s.color || '#e5e7eb';
  const o = s.opacity !== undefined ? s.opacity / 100 : 0.3;
  const dtype = s.divider_type || 'solid';
  if (dtype === 'space') return <div className="h-5" />;
  return (
    <div className="flex items-center gap-3 py-4" style={{ opacity: o }}>
      <div className="flex-1 h-px" style={{ backgroundColor: c }} />
      {dtype === 'circle' && <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: c }} />}
      {dtype === 'diamond' && <div className="w-2 h-2 rotate-45" style={{ backgroundColor: c }} />}
      {dtype === 'star' && <span style={{ color: c }}>★</span>}
      {['circle', 'diamond', 'star'].includes(dtype) && <div className="flex-1 h-px" style={{ backgroundColor: c }} />}
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const BannerBlock: React.FC<{ s: Record<string, any>; r: string }> = ({ s, r }) => {
  const src = s.url || s.image_url || getPublicFallbackImage(0);
  const image = (
    <SafeImage
      src={src}
      fallbackSrc={getPublicFallbackImage(0)}
      alt={s.alt_text || ''}
      className="w-full h-48 object-cover"
      iconSize={28}
    />
  );

  return (
    <div className="py-2">
      {s.link_url ? (
        <a href={s.link_url} target="_blank" rel="noopener noreferrer" className="block overflow-hidden shadow-md hover:shadow-lg transition" style={{ borderRadius: r }}>
          {image}
        </a>
      ) : (
        <div className="overflow-hidden shadow-md" style={{ borderRadius: r }}>
          {image}
        </div>
      )}
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
/** Image with graceful fallback — shows placeholder instead of broken icon on load error. */
const SafeImage: React.FC<{ src: string; alt?: string; className?: string; iconSize?: number; fallbackSrc?: string }> = ({ src, alt, className, iconSize = 20, fallbackSrc }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [src]);
  const resolvedSrc = failed && fallbackSrc ? fallbackSrc : src;
  if (!resolvedSrc) {
    return <div className={className || 'w-full h-full'}><div className="w-full h-full bg-black/5 flex items-center justify-center"><ImageIcon size={iconSize} className="opacity-20" /></div></div>;
  }
  return <img src={resolvedSrc} alt={alt || ''} className={className || 'w-full h-full object-cover'} onError={() => setFailed(true)} />;
};

const GalleryBlock: React.FC<{ s: Record<string, unknown>; r: string }> = ({ s, r }) => {
  const rawImgs: unknown[] = Array.isArray(s.images)
    ? s.images
    : Array.isArray(s.slides)
      ? s.slides
      : [];
  const imgs = rawImgs.length > 0
    ? rawImgs
    : [
      { url: getPublicFallbackImage(1) },
      { url: getPublicFallbackImage(2) },
    ];
  return (
    <div className="py-2 grid grid-cols-2 gap-2">
      {imgs.map((img: unknown, i: number) => {
        const url = typeof img === 'string'
          ? img
          : ((img as any).url || (img as any).image_url); // eslint-disable-line @typescript-eslint/no-explicit-any
        return (
          <div key={i} className="h-32 overflow-hidden shadow-sm" style={{ borderRadius: r }}>
            <SafeImage
              src={url || getPublicFallbackImage(i + 1)}
              fallbackSrc={getPublicFallbackImage(i + 1)}
              className="w-full h-full object-cover"
            />
          </div>
        );
      })}
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const VideoBlock: React.FC<{ s: Record<string, any>; r: string }> = ({ s, r }) => {
  const src = (s.source || '').toLowerCase();
  if (src === 'youtube' && s.url) {
    const yt = s.url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
    if (yt) {return (
      <div className="py-2"><div className="relative w-full aspect-video overflow-hidden shadow-lg" style={{ borderRadius: r }}>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <iframe title="YouTube video" src={`https://www.youtube.com/embed/${yt[1]}`} className="absolute inset-0 w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
      </div></div>
    );}
  }
  return (
    <div className="py-2"><div className="relative w-full aspect-video bg-gray-900 overflow-hidden flex items-center justify-center shadow-lg" style={{ borderRadius: r }}>
      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center"><Play size={28} className="text-white ml-1" fill="white" /></div>
      <span className="absolute top-3 right-3 text-[10px] text-white px-2.5 py-1 rounded-full font-bold" style={{ backgroundColor: src === 'youtube' ? '#FF0000' : '#555' }}>{src === 'youtube' ? 'YouTube' : 'Video'}</span>
    </div></div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const QrCodeBlock: React.FC<{ s: Record<string, any>; pageUrl?: string }> = ({ s, pageUrl }) => {
  const sz = ({ small: 130, medium: 170, large: 210 } as Record<string, number>)[s.qr_size || 'medium'] || 170;
  const url = s.url || pageUrl || window.location.href;
  return (
    <div className="flex justify-center py-4">
      <div className="bg-white p-4 rounded-2xl shadow-lg">
        <QRCodeSVG value={url} size={sz} level="M" fgColor={s.fg_color || '#1a1a1a'} bgColor={s.bg_color_qr || '#ffffff'} />
      </div>
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const WorkingHoursBlock: React.FC<{ s: Record<string, any>; r: string }> = ({ s, r }) => {
  const { strings } = useContext(LocaleCtx);
  const hours = s.hours as Record<string, { from?: string; to?: string; off?: boolean }> | undefined;
  const days: Array<{ day: string; open?: string; close?: string; is_closed?: boolean }> = useMemo(() => {
    const rawDays: unknown[] = Array.isArray(s.days)
      ? s.days
      : Array.isArray(s.schedule)
        ? s.schedule
        : [];
    if (hours && typeof hours === 'object') {
      const order = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      return order
        .filter((day) => hours[day])
        .map((day) => ({
          day,
          open: hours[day].from || '',
          close: hours[day].to || '',
          is_closed: hours[day].off || false,
        }));
    }
    return rawDays
      .map((entry) => {
        if (typeof entry === 'string') {
          const match = entry.match(/^([^:]+):\s*(.*)$/);
          const dayPart = match?.[1] ?? entry;
          const timePart = match?.[2] ?? '';
          return {
            day: dayPart.trim() || entry,
            open: timePart.trim(),
            close: '',
            is_closed: /closed/i.test(entry),
          };
        }
        const d = entry as Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
        return {
          day: String(d.day || ''),
          open: String(d.open || d.from || ''),
          close: String(d.close || d.to || ''),
          is_closed: Boolean(d.is_closed || d.off),
        };
      })
      .filter((entry) => entry.day);
  }, [hours, s.days, s.schedule]);

  if (!days.length) {
    return (
      <div className="py-2"><div className="bg-white/80 backdrop-blur p-5 shadow-sm text-center" style={{ borderRadius: r }}>
        <div className="flex items-center justify-center gap-2 opacity-40"><Clock size={14} /><span className="text-xs font-bold uppercase tracking-widest">{t(strings, 'hours_none', 'No hours set')}</span></div>
      </div></div>
    );
  }
  return (
    <div className="py-2"><div className="bg-white/80 backdrop-blur p-5 shadow-sm" style={{ borderRadius: r }}>
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-black/5"><Clock size={14} className="opacity-40" /><span className="text-xs font-bold uppercase tracking-widest opacity-40">{s.title || t(strings, 'hours_heading', 'Hours')}</span></div>
      {days.map((day, i: number) => {
        return (
          <div key={i} className="flex justify-between text-sm py-2 border-b border-black/[0.03] last:border-0">
            <span className="font-medium">{day.day}</span>
            <span className="opacity-50">
              {day.is_closed ? (
                <span className="text-red-400 font-medium">{t(strings, 'closed', 'Closed')}</span>
              ) : (
                [day.open, day.close].filter(Boolean).join(' – ')
              )}
            </span>
          </div>
        );
      })}
    </div></div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const TimerBlock: React.FC<{ s: Record<string, any>; pc: string }> = ({ s, pc }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const targetValue = s.end_date || s.target_date || s.targetDate;
    const parsed = targetValue ? new Date(targetValue) : null;
    // Use parsed date if valid, otherwise fallback to 7 days from now
    const target = parsed && !isNaN(parsed.getTime()) ? parsed : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

    const calc = () => {
      const diff = Math.max(0, target.getTime() - Date.now());
      if (diff === 0) { setExpired(true); return; }
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    calc();
    const interval = setInterval(calc, 1000);
    return () => clearInterval(interval);
  }, [s.end_date, s.target_date, s.targetDate]);

  if (expired) {
    return (
      <div className="flex justify-center py-4">
        <div className="px-6 py-3 rounded-2xl text-white font-bold shadow-lg" style={{ background: `linear-gradient(135deg, ${pc}, ${pc}bb)` }}>
          {s.expired_text || 'Time is up!'}
        </div>
      </div>
    );
  }

  const parts = [
    { v: String(timeLeft.days).padStart(2, '0'), l: 'Days' },
    { v: String(timeLeft.hours).padStart(2, '0'), l: 'Hrs' },
    { v: String(timeLeft.minutes).padStart(2, '0'), l: 'Min' },
    { v: String(timeLeft.seconds).padStart(2, '0'), l: 'Sec' },
  ];

  return (
    <div className="flex justify-center gap-3 py-4">
      {parts.map(({ v, l }) => (
        <div key={l} className="flex flex-col items-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-xl font-bold text-white shadow-lg" style={{ background: `linear-gradient(135deg, ${pc}, ${pc}bb)`, boxShadow: `0 4px 14px ${pc}40` }}>{v}</div>
          <span className="text-[10px] opacity-40 mt-1.5 font-semibold uppercase tracking-wide">{l}</span>
        </div>
      ))}
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MapBlock: React.FC<{ s: Record<string, any>; r: string }> = ({ s, r }) => {
  const { strings } = useContext(LocaleCtx);
  const tags: { address: string; color: string }[] = Array.isArray(s.tags) && s.tags.length > 0
    ? s.tags
    : s.address
      ? [{ address: String(s.address), color: s.marker_color || '#ff4400' }]
      : [];

  return (
    <div className="py-2">
      <div className="w-full h-56 bg-emerald-50 shadow-sm relative overflow-hidden flex flex-col items-center justify-center border border-emerald-100/50" style={{ borderRadius: r }}>
        {tags.length > 0 && tags[0].address ? (
          <iframe
            width="100%"
            height="100%"
            frameBorder="0"
            style={{ border: 0 }}
            src={`https://maps.google.com/maps?q=${encodeURIComponent(tags[0].address)}&t=&z=13&ie=UTF8&iwloc=&output=embed`}
            title={t(strings, 'map_title', 'Interactive Map')}
          />
        ) : (
          <div className="flex flex-col items-center gap-2 opacity-50">
             <MapPin size={28} className="text-emerald-500" />
             <p className="text-xs font-medium">{t(strings, 'map_no_location', 'No valid location pinned')}</p>
          </div>
        )}
      </div>
      {tags.length > 0 && tags.some(tag => tag.address) && (
        <div className="mt-3 px-2 space-y-1.5">
          {tags.map((tag, idx) => tag.address ? (
            <div key={idx} className="flex items-start gap-2">
              <div className="w-2 h-2 rounded-full mt-1.5 shrink-0 shadow-sm" style={{ backgroundColor: tag.color || '#ff4400' }} />
              <p className="text-sm font-medium text-gray-700 leading-tight">{tag.address}</p>
            </div>
          ) : null)}
        </div>
      )}
    </div>
  );
};

/* ─── P2-1 Image Carousel ─── */
// Horizontal scrollable carousel with optional autoplay + prev/next controls
// and dot indicators. Uses requestAnimationFrame-free interval for autoplay.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CarouselBlock: React.FC<{ s: Record<string, any>; r: string }> = ({ s, r }) => {
  const images: { url?: string; caption?: string; link?: string }[] =
    (Array.isArray(s.images) && s.images.length > 0
      ? s.images
      : Array.isArray(s.slides)
        ? s.slides.map((slide: Record<string, any>) => ({ // eslint-disable-line @typescript-eslint/no-explicit-any
            url: slide.url || slide.image_url,
            caption: slide.caption,
            link: slide.link || slide.link_url,
          }))
        : []) as { url?: string; caption?: string; link?: string }[];
  const autoplay = s.autoplay !== false;
  const interval = typeof s.interval_ms === 'number' && s.interval_ms > 0 ? s.interval_ms : 4000;
  const showArrows = s.show_arrows !== false;
  const showDots = s.show_dots !== false;
  const loop = s.loop !== false;
  const height = typeof s.height === 'number' && s.height > 0 ? s.height : 320;
  const rounded = s.rounded !== false;
  const [idx, setIdx] = useState(0);

  // Clamp index if images list shrinks
  useEffect(() => {
    if (idx >= images.length && images.length > 0) {
      setIdx(0);
    }
  }, [idx, images.length]);

  // Autoplay timer — paused if < 2 images
  useEffect(() => {
    if (!autoplay || images.length < 2) { return; }
    const handle = setInterval(() => {
      setIdx((prev) => {
        const next = prev + 1;
        if (next >= images.length) {
          return loop ? 0 : prev;
        }
        return next;
      });
    }, interval);
    return () => clearInterval(handle);
  }, [autoplay, interval, images.length, loop]);

  const goTo = (next: number) => {
    if (images.length === 0) { return; }
    if (next < 0) {
      setIdx(loop ? images.length - 1 : 0);
    } else if (next >= images.length) {
      setIdx(loop ? 0 : images.length - 1);
    } else {
      setIdx(next);
    }
  };

  if (images.length === 0) {
    return (
      <div className="py-2">
        <div
          className="w-full bg-black/5 flex items-center justify-center shadow-inner"
          style={{ borderRadius: rounded ? r : 0, height }}
        >
          <ImageIcon size={28} className="opacity-20" />
        </div>
      </div>
    );
  }

  const current = images[Math.min(idx, images.length - 1)];
  const inner = (
    <div
      className="relative w-full overflow-hidden shadow-md bg-black/5"
      style={{ borderRadius: rounded ? r : 0, height }}
    >
      {current.url ? (
        <img
          src={current.url}
          alt={current.caption || ''}
          className="w-full h-full object-cover transition-opacity duration-300"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <ImageIcon size={28} className="opacity-20" />
        </div>
      )}
      {current.caption && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white text-sm font-medium px-4 py-3">
          {current.caption}
        </div>
      )}
      {showArrows && images.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous"
            className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur flex items-center justify-center text-white transition"
            onClick={() => goTo(idx - 1)}
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            aria-label="Next"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur flex items-center justify-center text-white transition"
            onClick={() => goTo(idx + 1)}
          >
            <ChevronRight size={18} />
          </button>
        </>
      )}
      {showDots && images.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
          {images.map((_, i) => (
            <button
              type="button"
              key={i}
              aria-label={`Slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-5 bg-white' : 'w-1.5 bg-white/50'}`}
              onClick={() => setIdx(i)}
            />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="py-2">
      {current.link ? (
        <a
          href={current.link}
          target="_blank"
          rel="noopener noreferrer"
          className="block"
        >
          {inner}
        </a>
      ) : (
        inner
      )}
    </div>
  );
};

/* ─── P2-2 Music Embed ─── */
// Spotify / SoundCloud / Apple Music embed wrapper. Validates the URL's
// host before embedding to prevent arbitrary iframes — security baseline
// dictates we never render untrusted URLs in a sandboxed iframe.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MusicBlock: React.FC<{ s: Record<string, any>; r: string }> = ({ s, r }) => {
  const rawUrl = typeof s.url === 'string' ? s.url.trim() : '';
  const provider = (s.provider || 'spotify').toLowerCase();
  const compact = s.compact !== false;
  const theme = (s.theme || 'dark') as string;
  const height = typeof s.height === 'number' && s.height > 0 ? s.height : compact ? 80 : 352;

  // Allowed hosts per provider — rejects anything else for security.
  const allowedHosts: Record<string, RegExp> = {
    spotify: /^(open|www)\.spotify\.com$/,
    soundcloud: /^(www|m|api|w)\.soundcloud\.com$/,
    apple_music: /^(embed\.)?music\.apple\.com$/,
    apple: /^(embed\.)?music\.apple\.com$/,
  };

  let embedUrl = '';
  if (rawUrl) {
    try {
      const parsed = new URL(rawUrl);
      if (parsed.protocol !== 'https:') {
        embedUrl = '';
      } else if (provider === 'spotify' && allowedHosts.spotify.test(parsed.host)) {
        // https://open.spotify.com/track/... → https://open.spotify.com/embed/track/...
        const m = parsed.pathname.match(/^\/(track|album|playlist|episode|show|artist)\/([A-Za-z0-9]+)/);
        if (m) {
          embedUrl = `https://open.spotify.com/embed/${m[1]}/${m[2]}?theme=${theme === 'dark' ? '0' : '1'}`;
        }
      } else if ((provider === 'soundcloud') && allowedHosts.soundcloud.test(parsed.host)) {
        embedUrl = `https://w.soundcloud.com/player/?url=${encodeURIComponent(rawUrl)}&auto_play=false&color=%23ff5500`;
      } else if ((provider === 'apple_music' || provider === 'apple') && allowedHosts.apple.test(parsed.host)) {
        // https://music.apple.com/... → https://embed.music.apple.com/...
        embedUrl = rawUrl.replace(/^https:\/\/music\.apple\.com/, 'https://embed.music.apple.com');
      }
    } catch {
      embedUrl = '';
    }
  }

  const providerColor =
    provider === 'soundcloud'
      ? '#FF5500'
      : provider === 'apple_music' || provider === 'apple'
        ? '#FA2D48'
        : '#1DB954';

  if (!embedUrl) {
    return (
      <div className="py-2">
        <div
          className="w-full bg-white/80 backdrop-blur p-4 shadow-sm flex items-center gap-3"
          style={{ borderRadius: r }}
        >
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: providerColor }}
          >
            <Music size={18} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">
              {s.title || 'Music player'}
            </p>
            <p className="text-xs opacity-50 truncate">
              {rawUrl ? 'Invalid or unsupported URL' : 'No URL set'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-2">
      <div
        className="w-full overflow-hidden shadow-md"
        style={{ borderRadius: r }}
      >
        {/* eslint-disable-next-line jsx-a11y/iframe-has-title */}
        <iframe
          title={s.title || 'Music player'}
          src={embedUrl}
          width="100%"
          height={height}
          frameBorder="0"
          loading="lazy"
          sandbox="allow-scripts allow-same-origin allow-popups"
          allow="autoplay *; encrypted-media *; fullscreen *; clipboard-write"
        />
      </div>
    </div>
  );
};

/* ─── P2-3 FAQ Accordion ─── */
// Distinct from legacy 'questions' block — supports multiple-open mode,
// localized question/answer strings (content_i18n fallback), and a
// 'bordered' or 'minimal' visual style.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaqBlock: React.FC<{ s: Record<string, any>; r: string }> = ({ s, r }) => {
  const { locale, defaultLocale } = useContext(LocaleCtx);
  const items: unknown[] = useMemo(() => (s.items as unknown[]) || [], [s.items]);
  const allowMultiple = s.allow_multiple_open === true;
  const style = (s.style || 'bordered') as string;
  const title = s.title ? loc(s as Record<string, unknown>, 'title', locale, defaultLocale) : '';

  // Seed "open" set from items[].open on mount so first item expansion
  // is controllable from defaults.
  const seedOpen = useMemo(() => {
    const set = new Set<number>();
    items.forEach((item, i) => {
      const rec = item as Record<string, unknown>;
      if (rec.open === true) {
        set.add(i);
      }
    });
    return set;
  }, [items]);
  const [open, setOpen] = useState<Set<number>>(seedOpen);

  const toggle = (i: number) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(i)) {
        next.delete(i);
      } else {
        if (!allowMultiple) {
          next.clear();
        }
        next.add(i);
      }
      return next;
    });
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="py-2 space-y-2">
      {title && (
        <h3 className="text-base font-bold mb-1 px-1">{title}</h3>
      )}
      {items.map((item, i) => {
        const itemData = item as Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
        const question = loc(itemData, 'question', locale, defaultLocale);
        const answer = loc(itemData, 'answer', locale, defaultLocale);
        const isOpen = open.has(i);
        const base =
          style === 'minimal'
            ? 'bg-transparent border-b border-black/10'
            : 'bg-white/80 backdrop-blur shadow-sm';
        return (
          <div
            key={i}
            className={`overflow-hidden ${base}`}
            style={style === 'minimal' ? undefined : { borderRadius: r }}
          >
            <button
              type="button"
              onClick={() => toggle(i)}
              aria-expanded={isOpen}
              className="w-full flex items-center justify-between p-4 hover:bg-black/[0.02] transition text-left"
            >
              <span className="text-sm font-semibold flex-1 pr-3">
                {question || 'Question?'}
              </span>
              <ChevronDown
                size={16}
                className={`opacity-40 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {isOpen && answer && (
              <div className="px-4 pb-4 text-sm opacity-70 leading-relaxed">
                {answer}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ─── P2-4 Product Card ─── */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ProductBlock: React.FC<{ s: Record<string, any>; pc: string; r: string }> = ({ s, pc, r }) => {
  const { locale, defaultLocale, strings } = useContext(LocaleCtx);
  const currencySymbols: Record<string, string> = {
    USD: '$', EUR: '\u20AC', GBP: '\u00A3', JPY: '\u00A5',
    INR: '\u20B9', BRL: 'R$', CAD: 'C$', AUD: 'A$',
  };
  const currency = s.currency || 'USD';
  const symbol = currencySymbols[currency] || `${currency} `;
  const title =
    loc(s as Record<string, unknown>, 'title', locale, defaultLocale) ||
    loc(s as Record<string, unknown>, 'name', locale, defaultLocale) ||
    'Product';
  const subtitle =
    loc(s as Record<string, unknown>, 'subtitle', locale, defaultLocale) ||
    loc(s as Record<string, unknown>, 'tagline', locale, defaultLocale);
  const description = loc(s as Record<string, unknown>, 'description', locale, defaultLocale);
  const ctaLabel =
    loc(s as Record<string, unknown>, 'cta_label', locale, defaultLocale) ||
    loc(s as Record<string, unknown>, 'button_text', locale, defaultLocale) ||
    t(strings, 'product_buy_now', 'Buy now');
  const price =
    s.price !== null && s.price !== undefined && s.price !== ''
      ? `${symbol}${s.price}`
      : `${symbol}0`;
  const compareAt =
    s.compare_at_price !== null &&
    s.compare_at_price !== undefined &&
    s.compare_at_price !== ''
      ? `${symbol}${s.compare_at_price}`
      : '';
  const badge = s.badge || '';
  const imgUrl = s.image_url;
  const inStock = s.in_stock !== false;
  const openNewTab = s.open_new_tab !== false;
  const ctaUrl = typeof s.cta_url === 'string' ? s.cta_url : typeof s.buy_url === 'string' ? s.buy_url : '';

  // Validate CTA URL before rendering — never render arbitrary href.
  const safeCtaUrl = useMemo(() => {
    if (!ctaUrl) { return ''; }
    try {
      const parsed = new URL(ctaUrl, window.location.origin);
      if (!['http:', 'https:', 'mailto:'].includes(parsed.protocol)) {
        return '';
      }
      return ctaUrl;
    } catch {
      return '';
    }
  }, [ctaUrl]);

  const inner = (
    <div
      className="bg-white/90 backdrop-blur overflow-hidden shadow-md hover:shadow-lg transition"
      style={{ borderRadius: r }}
    >
      <div className="relative aspect-[4/3] bg-black/5">
        {imgUrl ? (
          <img
            src={imgUrl}
            alt={title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ShoppingBag size={32} className="opacity-20" />
          </div>
        )}
        {badge && (
          <span
            className="absolute top-3 left-3 text-xs font-bold text-white px-2.5 py-1 rounded-full shadow-md"
            style={{ backgroundColor: pc }}
          >
            {badge}
          </span>
        )}
        {!inStock && (
          <span className="absolute top-3 right-3 text-xs font-bold text-white bg-gray-700 px-2.5 py-1 rounded-full shadow-md">
            {t(strings, 'product_out_of_stock', 'Out of stock')}
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-base font-bold">{title}</h3>
        {subtitle && <p className="text-xs opacity-60 mt-0.5">{subtitle}</p>}
        {description && (
          <p className="text-sm opacity-70 mt-2 leading-relaxed">{description}</p>
        )}
        <div className="flex items-baseline gap-2 mt-3">
          <span className="text-lg font-bold">{price}</span>
          {compareAt && (
            <span className="text-sm opacity-50 line-through">{compareAt}</span>
          )}
        </div>
        <div
          className="mt-3 w-full h-11 rounded-xl flex items-center justify-center text-sm font-bold text-white shadow-md transition"
          style={{
            backgroundColor: inStock ? pc : '#9ca3af',
            opacity: inStock ? 1 : 0.8,
          }}
        >
          {ctaLabel}
        </div>
      </div>
    </div>
  );

  if (safeCtaUrl && inStock) {
    return (
      <div className="py-2">
        <a
          href={safeCtaUrl}
          target={openNewTab ? '_blank' : undefined}
          rel={openNewTab ? 'noopener noreferrer' : undefined}
          className="block"
        >
          {inner}
        </a>
      </div>
    );
  }
  return <div className="py-2">{inner}</div>;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const QuestionsBlock: React.FC<{ s: Record<string, any>; r: string }> = ({ s, r }) => {
  const { locale, defaultLocale } = useContext(LocaleCtx);
  const items: unknown[] = (s.items as unknown[]) || [{ question: 'FAQ question?' }];
  const [open, setOpen] = useState<number | null>(null);
  return (
    <div className="py-2 space-y-2">
      {items.map((item: unknown, i: number) => {
        const itemData = item as Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
        const question = loc(itemData, 'question', locale, defaultLocale);
        const answer = loc(itemData, 'answer', locale, defaultLocale);
        return (
          <div key={i} className="bg-white/80 backdrop-blur overflow-hidden shadow-sm" style={{ borderRadius: r }}>
            <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-4 hover:bg-black/[0.02] transition">
              <span className="text-sm font-semibold text-left">{question || 'Question?'}</span>
              <ChevronDown size={16} className={`opacity-30 transition-transform ml-2 flex-shrink-0 ${open === i ? 'rotate-180' : ''}`} />
            </button>
            {open === i && answer && <div className="px-4 pb-4 text-sm opacity-50 leading-relaxed">{answer}</div>}
          </div>
        );
      })}
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FormBlock: React.FC<{ s: Record<string, any>; blockId: string; pc: string; r: string; pageSlug?: string; isPreview?: boolean }> = ({ s, blockId, pc, r, pageSlug, isPreview = false }) => {
  const { strings, locale, defaultLocale } = useContext(LocaleCtx);
  const trackPageEvent = useContext(PageTrackCtx);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [previewSubmitted, setPreviewSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  // Honeypot — bots will fill any non-empty input; humans never see it.
  const [honeypot, setHoneypot] = useState<string>('');
  // Field-level error surfaced by the server validator (missing-required,
  // invalid-email, too-long, etc.).
  const [fieldError, setFieldError] = useState<{ field: string | null; message: string } | null>(null);

  // Field schema: honor the block's declared fields, falling back to
  // the legacy three-field defaults for pages that haven't been
  // migrated to the new schema editor yet.
  //
  // CRITICAL: every field MUST have a unique, stable ``name`` —
  // the controlled ``<input value={formData[f.name]} />`` pattern
  // collapses every field to ``formData[undefined]`` if ``name`` is
  // missing, which means typing in one input cascades into all of
  // them. Legacy blocks only set ``label``, so we synthesize a
  // slug-cased name here (matching the backend's
  // ``_block_field_specs`` synthesis so the submit payload aligns).
  const rawFields: Array<{ name?: string; label?: string; type?: string; required?: boolean; options?: string[] }> =
    Array.isArray(s.fields) && s.fields.length > 0
      ? s.fields
      : [
          { name: 'name', label: t(strings, 'form_name_placeholder', 'Your name'), type: 'text' },
          { name: 'email', label: t(strings, 'form_email_placeholder', 'Email address'), type: 'email', required: true },
          { name: 'message', label: t(strings, 'form_message_placeholder', 'Message...'), type: 'textarea' },
        ];
  const fields: Array<{ name: string; label: string; type: string; required?: boolean; options?: string[] }> = useMemo(() => {
    const out: Array<{ name: string; label: string; type: string; required?: boolean; options?: string[] }> = [];
    const seen = new Set<string>();
    rawFields.forEach((f, idx) => {
      const rawName = typeof f.name === 'string' ? f.name.trim() : '';
      let candidate = rawName;
      if (!candidate) {
        // Slug-case the label — matches the backend synthesis so the
        // answers-dict key the server receives matches the spec.name
        // it expects.
        const base = String(f.label || `field_${idx + 1}`).trim().toLowerCase();
        candidate = Array.from(base)
          .map((c) => (/[a-z0-9]/.test(c) ? c : '_'))
          .join('')
          .replace(/_+/g, '_')
          .replace(/^_+|_+$/g, '') || `field_${idx + 1}`;
      }
      // Dedupe collisions deterministically so React keys stay stable
      // across renders for the same input shape.
      let unique = candidate;
      let n = 2;
      while (seen.has(unique)) {
        unique = `${candidate}_${n}`;
        n += 1;
      }
      seen.add(unique);
      out.push({
        name: unique,
        label: String(f.label || unique),
        type: String(f.type || 'text'),
        required: !!f.required,
        options: Array.isArray(f.options) ? f.options : undefined,
      });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(rawFields)]);

  const requireCaptcha = s.require_captcha !== false;
  const honeypotField = s.honeypot_field || 'url';
  const turnstileSiteKey = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string) || '';
  const formTitle = loc(s as Record<string, unknown>, 'title', locale, defaultLocale);
  const formDescription =
    loc(s as Record<string, unknown>, 'description', locale, defaultLocale) ||
    loc(s as Record<string, unknown>, 'subtitle', locale, defaultLocale);

  const handleSubmit = async () => {
    setFieldError(null);
    // Client-side required check — the server re-validates authoritatively.
    for (const f of fields) {
      if (f.required && !(formData[f.name] || '').trim()) {
        setFieldError({ field: f.name, message: `${f.label || f.name} is required` });
        return;
      }
    }
    if (requireCaptcha && turnstileSiteKey && !turnstileToken) {
      // Not localized — safety net for the small window before we add
      // a localized key. Users normally see the Turnstile widget itself.
      toast.error('Please complete the CAPTCHA to continue.');
      return;
    }

    if (isPreview) {
      setPreviewSubmitted(true);
      setSubmitted(true);
      toast.success(t(strings, 'form_preview_not_saved', 'Preview only. No submission was saved.'));
      return;
    }

    setSubmitting(true);
    try {
      // The new submit shape includes block_id (so the server can
      // resolve field schema), turnstile_token, and optional honeypot.
      const resp = await pagesAPI.submitForm(pageSlug || '', {
        block_id: blockId,
        answers: { ...formData, [honeypotField]: honeypot },
        turnstile_token: turnstileToken || undefined,
        honeypot: honeypot || undefined,
      });
      const data = (resp?.data || {}) as { redirect_url?: string };
      trackPageEvent({
        event_type: 'form_submit',
        block_id: blockId,
        block_type: 'form',
        metadata: { redirected: Boolean(data.redirect_url) },
      });
      if (data.redirect_url) {
        window.location.assign(data.redirect_url);
        return;
      }
      setSubmitted(true);
      toast.success(s.success_message || t(strings, 'form_sent', 'Message sent!'));
    } catch (err: unknown) {
      // Surface the server's field-level error when available.
      const e = err as { response?: { data?: { detail?: { code?: string; field?: string | null; message?: string } | string } } };
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.message) {
        setFieldError({ field: detail.field ?? null, message: detail.message });
        toast.error(detail.message);
      } else {
        toast.error(t(strings, 'form_error', 'Failed to send. Please try again.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="py-2"><div className="bg-white/80 backdrop-blur p-5 shadow-sm text-center" style={{ borderRadius: r }}>
        <div className="text-3xl mb-2">✓</div>
        <p className="text-sm font-semibold">
          {previewSubmitted
            ? t(strings, 'form_preview_thank_you', 'Preview submitted')
            : (s.success_message || t(strings, 'form_thank_you', 'Thank you!'))}
        </p>
        <p className="text-xs opacity-50 mt-1">
          {previewSubmitted
            ? t(strings, 'form_preview_not_saved', 'Preview only. No submission was saved.')
            : t(strings, 'form_sent_desc', 'Your message has been sent.')}
        </p>
      </div></div>
    );
  }

  return (
    <div className="py-2">
      <div className="bg-white/80 backdrop-blur p-5 space-y-3 shadow-sm" style={{ borderRadius: r }}>
        {(formTitle || formDescription) && (
          <div className="space-y-1 text-center">
            {formTitle && <h3 className="text-base font-bold">{formTitle}</h3>}
            {formDescription && (
              <p className="text-xs opacity-60 leading-relaxed">{formDescription}</p>
            )}
          </div>
        )}

        {fields.map((f) => {
          const errored = fieldError?.field === f.name;
          const ringStyle: React.CSSProperties = { '--tw-ring-color': errored ? '#ef4444' : pc } as React.CSSProperties;
          if (f.type === 'textarea') {
            return (
              <textarea
                key={f.name}
                className="w-full h-24 rounded-xl bg-black/[0.03] border-0 px-4 py-3 text-sm resize-none placeholder:opacity-40 focus:ring-2 focus:outline-none transition"
                style={ringStyle}
                placeholder={f.label}
                value={formData[f.name] || ''}
                onChange={(e) => setFormData(p => ({ ...p, [f.name]: e.target.value }))}
                aria-invalid={errored}
              />
            );
          }
          if (f.type === 'select') {
            return (
              <select
                key={f.name}
                className="w-full h-11 rounded-xl bg-black/[0.03] border-0 px-4 text-sm focus:ring-2 focus:outline-none transition"
                style={ringStyle}
                value={formData[f.name] || ''}
                onChange={(e) => setFormData(p => ({ ...p, [f.name]: e.target.value }))}
                aria-invalid={errored}
              >
                <option value="" disabled>{f.label}</option>
                {(f.options || []).map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            );
          }
          return (
            <input
              key={f.name}
              type={f.type || 'text'}
              className="w-full h-11 rounded-xl bg-black/[0.03] border-0 px-4 text-sm placeholder:opacity-40 focus:ring-2 focus:outline-none transition"
              style={ringStyle}
              placeholder={f.label}
              value={formData[f.name] || ''}
              onChange={(e) => setFormData(p => ({ ...p, [f.name]: e.target.value }))}
              aria-invalid={errored}
            />
          );
        })}

        {fieldError?.message && (
          <p className="text-xs text-red-500" role="alert">{fieldError.message}</p>
        )}

        {/*
          Honeypot — rendered off-screen with autocomplete disabled.
          Humans won't see it; bots that brute-fill every input will
          trigger the spam tarpit on the server.
        */}
        <label
          htmlFor={`hp-${blockId}`}
          style={{ position: 'absolute', left: '-10000px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}
          aria-hidden="true"
        >
          {honeypotField}
          <input
            id={`hp-${blockId}`}
            name={honeypotField}
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </label>

        {/* Turnstile widget — renders nothing when site key is absent (dev). */}
        {requireCaptcha && turnstileSiteKey && (
          <TurnstileWidget
            siteKey={turnstileSiteKey}
            onToken={setTurnstileToken}
            action={`form-${blockId}`}
            className="pt-1"
          />
        )}

        <button
          onClick={handleSubmit}
          disabled={submitting}
          style={{ backgroundColor: pc, borderRadius: r }}
          className="w-full py-3 text-white font-semibold text-sm shadow-md hover:shadow-lg transition disabled:opacity-60"
          aria-busy={submitting}
        >
          {submitting
            ? t(strings, 'form_sending', 'Sending...')
            : (s.submit_text || s.submit_label || s.btn_text || t(strings, 'form_submit', 'Send Message'))}
        </button>
      </div>
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PaymentBlock: React.FC<{ s: Record<string, any>; pc: string; r: string; pageSlug?: string; isPreview?: boolean }> = ({ s, pc, r, pageSlug, isPreview = false }) => {
  const { strings } = useContext(LocaleCtx);
  const trackPageEvent = useContext(PageTrackCtx);
  const [loading, setLoading] = useState(false);

  const handlePay = async () => {
    if (isPreview) {
      toast.success(t(strings, 'payment_preview_not_started', 'Preview only. Checkout was not started.'));
      return;
    }
    trackPageEvent({
      event_type: 'payment_start',
      block_id: s.id as string | undefined,
      block_type: 'payment',
      link_url: s.payment_url || undefined,
      link_label: s.button_text || s.title || 'Payment',
    });
    if (s.stripe_price_id) {
      setLoading(true);
      try {
        const { data } = await pagesAPI.createCheckout(pageSlug || '', s.stripe_price_id);
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        }
      } catch {
        toast.error(t(strings, 'payment_not_configured', 'Payment is not configured yet.'));
      } finally {
        setLoading(false);
      }
    } else if (s.payment_url) {
      window.open(s.payment_url, '_blank');
    } else {
      toast.error(t(strings, 'payment_not_configured', 'Payment not configured'));
    }
  };

  return (
    <div className="py-2"><div className="bg-white/80 backdrop-blur p-5 flex items-center gap-4 shadow-sm" style={{ borderRadius: r }}>
      <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-sm" style={{ backgroundColor: `${pc}15` }}><CreditCard size={20} style={{ color: pc }} /></div>
      <div className="flex-1">
        <p className="text-sm font-bold">{s.title || 'Payment'}</p>
        <p className="text-xs opacity-40 mt-0.5">{s.amount || '$0.00'}</p>
        {s.description && <p className="text-xs opacity-50 mt-1">{s.description}</p>}
      </div>
      <button onClick={handlePay} disabled={loading} style={{ backgroundColor: pc }} className="text-sm text-white px-5 py-2.5 rounded-xl font-semibold shadow-md hover:shadow-lg transition disabled:opacity-60">
        {loading ? '...' : (s.button_text || t(strings, 'pay', 'Pay'))}
      </button>
    </div></div>
  );
};

// P3-2 Tip jar block — Stripe-backed donation widget. Mirrors the legacy
// PaymentBlock pattern but lets the visitor pick the amount at click time
// (preset buttons + optional free-form input + optional message). The
// backend route (``POST /p/{slug}/tip-jar/checkout``) creates a Stripe
// Checkout Session on the fly using the connected account's ``acct_...``
// id stored in block settings and redirects the visitor to Stripe.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const TipJarBlock: React.FC<{ s: Record<string, any>; pc: string; r: string; pageSlug?: string; isPreview?: boolean }> = ({ s, pc, r, pageSlug, isPreview = false }) => {
  const { strings } = useContext(LocaleCtx);
  const trackPageEvent = useContext(PageTrackCtx);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);
  const [custom, setCustom] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [done, setDone] = useState(false);

  // Presets — fall back to $3/$5/$10 if settings are blank/invalid.
  const presets: number[] = useMemo(() => {
    const raw: unknown[] = Array.isArray(s.preset_amounts)
      ? s.preset_amounts
      : Array.isArray(s.presets)
        ? s.presets.map((preset: Record<string, unknown>) => preset.amount)
        : [];
    const nums = raw
      .map((x) => (typeof x === 'number' ? x : parseFloat(String(x))))
      .filter((n) => Number.isFinite(n) && n > 0);
    return nums.length ? nums.slice(0, 6) : [3, 5, 10];
  }, [s.preset_amounts, s.presets]);

  const allowCustom: boolean = s.allow_custom_amount !== false;
  const allowMessage: boolean = s.allow_message !== false;
  const currencySymbol: string = (s.currency_symbol as string) || '$';
  const currency: string = ((s.currency as string) || 'USD').toUpperCase();
  const headline: string = (s.headline as string) || t(strings, 'tip_jar_headline', 'Buy me a coffee');
  const description: string = (s.message as string) || '';
  const buttonLabel: string = (s.button_label as string) || (s.button_text as string) || t(strings, 'tip_jar_send_tip', 'Send tip');
  const thankYou: string = (s.thank_you_message as string) || t(strings, 'tip_jar_thank_you', 'Thank you for your support!');
  const messagePlaceholder: string = (s.message_placeholder as string) || t(strings, 'tip_jar_message_placeholder', 'Leave a message (optional)');
  const iconName: string = (s.icon as string) || 'coffee';
  const Icon = iconName === 'heart' ? Heart : Coffee;

  const selectedAmount = useMemo(() => {
    if (selected !== null) { return presets[selected]; }
    if (!allowCustom) { return null; }
    const parsed = parseFloat(custom);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [selected, custom, presets, allowCustom]);

  const handleTip = async () => {
    if (selectedAmount === null) {
      toast.error(t(strings, 'tip_jar_pick_amount', 'Please pick an amount.'));
      return;
    }
    if (!s.stripe_account_id) {
      toast.error(t(strings, 'tip_jar_not_configured', 'Tip jar is not configured yet.'));
      return;
    }
    if (isPreview) {
      toast.success(t(strings, 'tip_jar_preview_not_started', 'Preview only. Tip checkout was not started.'));
      return;
    }
    trackPageEvent({
      event_type: 'tip_start',
      block_id: s.id as string | undefined,
      block_type: 'tip_jar',
      link_label: headline,
      metadata: { amount: selectedAmount, currency },
    });
    setLoading(true);
    try {
      const { data } = await pagesAPI.createTipJarCheckout(pageSlug || '', {
        amount: selectedAmount,
        currency,
        block_id: s.id as string | undefined,
        message: allowMessage && message.trim() ? message.trim().slice(0, 500) : undefined,
      });
      if (data?.checkout_url) {
        setDone(true);
        window.location.href = data.checkout_url as string;
      } else {
        toast.error(t(strings, 'tip_jar_not_configured', 'Tip jar is not configured yet.'));
      }
    } catch {
      toast.error(t(strings, 'tip_jar_not_configured', 'Tip jar is not configured yet.'));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="py-2"><div className="bg-white/80 backdrop-blur p-6 shadow-sm text-center" style={{ borderRadius: r }}>
        <div className="w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-2" style={{ backgroundColor: `${pc}15` }}>
          <Heart size={22} style={{ color: pc }} />
        </div>
        <p className="text-sm font-semibold">{thankYou}</p>
      </div></div>
    );
  }

  return (
    <div className="py-2"><div className="bg-white/80 backdrop-blur p-5 shadow-sm space-y-3" style={{ borderRadius: r }}>
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0" style={{ backgroundColor: `${pc}15` }}>
          <Icon size={20} style={{ color: pc }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold truncate">{headline}</p>
          {description && <p className="text-xs opacity-50 mt-0.5 line-clamp-2">{description}</p>}
        </div>
      </div>

      {/* Preset buttons */}
      <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(presets.length, 3)}, 1fr)` }}>
        {presets.map((amt, i) => {
          const active = selected === i;
          return (
            <button
              key={`${amt}-${i}`}
              type="button"
              onClick={() => { setSelected(i); setCustom(''); }}
              aria-pressed={active}
              className="py-2.5 text-sm font-semibold border-2 transition"
              style={{
                borderRadius: r,
                borderColor: active ? pc : 'transparent',
                backgroundColor: active ? `${pc}15` : 'rgba(0,0,0,0.035)',
                color: active ? pc : undefined,
              }}
            >
              {currencySymbol}{amt}
            </button>
          );
        })}
      </div>

      {/* Custom amount */}
      {allowCustom && (
        <div className="flex items-center gap-2">
          <label htmlFor={`tip-jar-custom-${s.id || 'block'}`} className="sr-only">
            {t(strings, 'tip_jar_custom', 'Custom amount')}
          </label>
          <div className="flex-1 relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm opacity-50 pointer-events-none">{currencySymbol}</span>
            <input
              id={`tip-jar-custom-${s.id || 'block'}`}
              type="number"
              inputMode="decimal"
              min={s.min_amount ?? 1}
              max={s.max_amount ?? 500}
              step="0.01"
              value={custom}
              onChange={(e) => { setCustom(e.target.value); setSelected(null); }}
              placeholder={t(strings, 'tip_jar_custom', 'Custom amount')}
              className="w-full h-11 rounded-xl bg-black/[0.03] border-0 pl-8 pr-4 text-sm placeholder:opacity-40 focus:ring-2 focus:outline-none transition"
              style={{ '--tw-ring-color': pc } as React.CSSProperties}
            />
          </div>
        </div>
      )}

      {/* Optional message */}
      {allowMessage && (
        <div>
          <label htmlFor={`tip-jar-msg-${s.id || 'block'}`} className="sr-only">{messagePlaceholder}</label>
          <textarea
            id={`tip-jar-msg-${s.id || 'block'}`}
            className="w-full h-20 rounded-xl bg-black/[0.03] border-0 px-4 py-3 text-sm resize-none placeholder:opacity-40 focus:ring-2 focus:outline-none transition"
            style={{ '--tw-ring-color': pc } as React.CSSProperties}
            placeholder={messagePlaceholder}
            maxLength={500}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>
      )}

      {/* Send button */}
      <button
        type="button"
        onClick={handleTip}
        disabled={loading || selectedAmount === null}
        aria-busy={loading}
        style={{ backgroundColor: pc, borderRadius: r }}
        className="w-full py-3 text-white font-semibold text-sm shadow-md hover:shadow-lg transition disabled:opacity-60"
      >
        {loading
          ? t(strings, 'tip_jar_processing', 'Processing...')
          : selectedAmount !== null
            ? `${buttonLabel} — ${currencySymbol}${selectedAmount}`
            : buttonLabel}
      </button>
    </div></div>
  );
};

const normalizePublicHtml = (content: string): string => (
  content
    .replace(
      /height:\s*260px;\s*object-fit:\s*cover;\s*display:\s*block;/g,
      'aspect-ratio:1.22;object-fit:cover;object-position:center top;display:block;',
    )
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const HtmlBlock: React.FC<{ s: Record<string, any> }> = ({ s }) => {
  const content = s.html_content || s.code || s.content;
  if (content) {
    const normalizedContent = normalizePublicHtml(String(content));
    return (
      <div
        className="py-2 [&_img]:max-w-full"
        dangerouslySetInnerHTML={{ __html: normalizedContent }}
      />
    );
  }
  return <div className="py-3 flex items-center justify-center opacity-20"><Code size={16} className="mr-2" /><span className="text-sm">HTML</span></div>;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ColumnsBlock: React.FC<{ s: Record<string, any>; r: string }> = ({ s, r }) => {
  const cols = s.columns || 2;
  const columnData: unknown[] = (s.column_data as unknown[]) || [];
  return (
    <div className="py-2" style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '12px' }}>
      {[...Array(cols)].map((_, i) => {
        const colContent = columnData[i] as any; // eslint-disable-line @typescript-eslint/no-explicit-any
        if (colContent) {
          const isHtml = typeof colContent.content === 'string' && colContent.content.includes('<');
          return (
            <div key={i} className="bg-white/60 shadow-sm p-3" style={{ borderRadius: r }}>
              {colContent.title && <h4 className="font-semibold text-sm mb-1">{colContent.title}</h4>}
              {colContent.image_url && <SafeImage src={colContent.image_url} className="w-full h-24 object-cover rounded mb-2" />}
              {colContent.content && (
                isHtml
                  ? <div className="text-sm opacity-70" dangerouslySetInnerHTML={{ __html: colContent.content }} />
                  : <p className="text-sm opacity-70">{colContent.content}</p>
              )}
            </div>
          );
        }
        return <div key={i} className="h-20 bg-white/60 shadow-sm" style={{ borderRadius: r }} />;
      })}
    </div>
  );
};

// P3-3 Custom modular grid — Taplink-style per-cell layout where each
// cell carries its own icon/emoji, title, subtitle, optional image, and
// optional link. Unlike the legacy ColumnsBlock (shared content, fixed
// equal widths) this gives page owners a card grid they can use for
// services, team members, category navigation, or anything visual.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const GridBlock: React.FC<{ s: Record<string, any>; pc: string; r: string }> = ({ s, r }) => {
  const cols: number = Math.max(1, Math.min(4, Number(s.columns) || 2));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cells: any[] = Array.isArray(s.cells) ? s.cells : [];
  const gapMap: Record<string, string> = { small: '8px', medium: '12px', large: '16px' };
  const gap = gapMap[(s.gap as string) || 'medium'] || '12px';
  const isCard = (s.cell_style as string) !== 'flat';

  if (!cells.length) {
    return (
      <div className="py-3 flex items-center justify-center opacity-20">
        <span className="text-sm">Grid</span>
      </div>
    );
  }

  return (
    <div className="py-2">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gap,
        }}
      >
        {cells.map((cell, i) => {
          const icon: string = (cell?.icon as string) || '';
          const title: string = (cell?.title as string) || '';
          const subtitle: string = (cell?.subtitle as string) || '';
          const imageUrl: string = (cell?.image_url as string) || '';
          const linkUrl: string = (cell?.link_url as string) || '';

          const inner = (
            <div
              className={isCard
                ? 'bg-white/70 backdrop-blur shadow-sm p-4 flex flex-col items-center text-center gap-1.5 h-full transition hover:shadow-md'
                : 'p-3 flex flex-col items-center text-center gap-1 h-full'}
              style={{ borderRadius: r }}
            >
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt={title || ''}
                  className="w-full h-20 object-cover rounded mb-1"
                  loading="lazy"
                />
              )}
              {icon && (
                <span className="text-2xl leading-none select-none" aria-hidden="true">
                  {icon}
                </span>
              )}
              {title && <p className="text-sm font-semibold leading-snug">{title}</p>}
              {subtitle && <p className="text-xs opacity-50 leading-snug">{subtitle}</p>}
            </div>
          );

          if (linkUrl) {
            return (
              <a
                key={`grid-${i}`}
                href={linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="no-underline"
                style={{ color: 'inherit' }}
              >
                {inner}
              </a>
            );
          }

          return <div key={`grid-${i}`}>{inner}</div>;
        })}
      </div>
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const LanguageSwitcherBlock: React.FC<{ s: Record<string, any>; pc: string }> = ({ s, pc }) => {
  const { locale, availableLocales, strings } = useContext(LocaleCtx);
  const [searchParams, setSearchParams] = useSearchParams();

  // Use block-level override list, or fall back to page available_locales
  const locales: string[] = (s.locales as string[]) || availableLocales;
  if (!locales.length) return null;

  const supportedMap = Object.fromEntries(SUPPORTED_LOCALES.map(l => [l.code, l]));

  return (
    <div className="py-3">
      <div className="flex items-center justify-center gap-1.5 mb-2 opacity-40">
        <Globe size={12} />
        <span className="text-xs font-medium">{t(strings, 'choose_language', 'Language')}</span>
      </div>
      <div className="flex justify-center gap-2 flex-wrap">
        {locales.map(code => {
          const info = supportedMap[code];
          const isActive = code === locale;
          return (
            <button
              key={code}
              onClick={() => {
                const p = new URLSearchParams(searchParams);
                p.set('lang', code);
                setSearchParams(p);
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${isActive ? 'text-white shadow-md' : 'bg-black/5 hover:bg-black/10'}`}
              style={isActive ? { backgroundColor: pc } : {}}
              title={info?.label || code}
            >
              {info?.nativeLabel || code.toUpperCase()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   Main Public Page
   ═══════════════════════════════════════════════════════════ */

const PublicPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [strings, setStrings] = useState<PublicLocaleStrings | null>(null);

  const pwd = searchParams.get('pwd') || '';
  const langParam = searchParams.get('lang') || '';
  const previewParam = searchParams.get('preview') || '';
  const tokenParam = searchParams.get('token') || '';

  const { data, isLoading, error } = useQuery({
    queryKey: ['public-page', slug, pwd, langParam, previewParam, tokenParam],
    queryFn: () => pagesAPI.getPublic(slug!, {
      ...(pwd ? { pwd } : {}),
      ...(langParam ? { lang: langParam } : {}),
      ...(previewParam ? { preview: previewParam } : {}),
      ...(tokenParam ? { token: tokenParam } : {}),
    }).then(r => r.data),
    enabled: !!slug,
    retry: (failureCount, queryError) => {
      if (isTerminalPublicPageError(queryError)) {
        return false;
      }
      return failureCount < 2;
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const page: PublicPageData | undefined = data?.page;

  const trackPageEvent = useCallback((payload: PublicPageEventPayload) => {
    if (!slug || page?.is_preview) { return; }
    sendPublicPageEvent(slug, searchParams, payload);
  }, [page?.is_preview, searchParams, slug]);

  useEffect(() => {
    if (!page || page.password_protected || page.is_preview) { return; }
    trackPageEvent({ event_type: 'page_view' });
  }, [page, trackPageEvent]);

  const handleBlockClickCapture = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    const blockEl = event.currentTarget;
    const anchor = target?.closest('a[href]') as HTMLAnchorElement | null;
    if (!anchor) { return; }
    const href = anchor.getAttribute('href') || '';
    if (!href || href === '#') { return; }
    trackPageEvent({
      event_type: 'block_click',
      block_id: blockEl.dataset.pageBlockId,
      block_type: blockEl.dataset.pageBlockType,
      link_url: href,
      link_label: (anchor.textContent || anchor.getAttribute('aria-label') || anchor.title || '').trim().slice(0, 200),
    });
  }, [trackPageEvent]);

  // Resolve locale: URL param → page default
  const defaultLocale = page?.default_locale || 'en';
  const availableLocales: string[] = page?.available_locales || [];
  const locale = (() => {
    if (langParam && (langParam === defaultLocale || availableLocales.includes(langParam))) {
      return langParam;
    }
    return page?.locale || defaultLocale;
  })();

  const isRtl = RTL_LOCALES.has(locale);

  // Load locale strings whenever locale changes
  useEffect(() => {
    loadLocale(locale).then(setStrings);
  }, [locale]);

  // Theme helper: StylingPanel writes camelCase keys; backend tone defaults
  // use snake_case. Read both forms so styles apply regardless of origin.
  const pt = page?.theme || {};
  const themeVal = (camel: string, snake: string, fallback = ''): string => {
    const v = pt[camel] ?? pt[snake];
    return typeof v === 'string' ? v : (typeof v === 'number' ? String(v) : fallback);
  };

  const pc = themeVal('primaryColor', 'primary_color', '#4CAF50');
  const bg = themeVal('backgroundColor', 'background_color', '#f0f2f5');
  const tc = themeVal('fontColor', 'text_color', '#1a1a1a');
  // P1.5: backend emits explicit heading/body font aliases on every theme
  // (from FONT_PAIRS_BY_TONE) so we can load both from Google Fonts and
  // apply them separately. Fall back to the legacy `font_family` key for
  // pages persisted before P1.5 rolled out.
  // fontFamily (from StylingPanel picker) wins over body_font_family
  // (from tone) so a manual font selection always takes effect.
  const bodyFontName: string =
    themeVal('fontFamily', 'font_family') ||
    themeVal('bodyFontFamily', 'body_font_family') ||
    'Inter';
  const headingFontName: string =
    themeVal('headingFontFamily', 'heading_font_family') ||
    themeVal('displayFontFamily', 'display_font_family') ||
    bodyFontName;
  const ff = wrapFontFamily(bodyFontName);
  const headingFf = wrapFontFamily(headingFontName);
  const r = themeVal('borderRadius', 'border_radius', '16px');
  const lc = themeVal('linkColor', 'link_color') || pc;
  const bgImage = themeVal('backgroundImage', 'background_image');
  const customCss = themeVal('customCSS', 'custom_css');

  // Gradient background (StylingPanel writes camelCase)
  const gradientStart = themeVal('gradientStart', 'gradient_start');
  const gradientEnd = themeVal('gradientEnd', 'gradient_end');
  const gradientDirection = themeVal('gradientDirection', 'gradient_direction', 'to bottom');

  // Layout / spacing
  const pagePadding = themeVal('pagePadding', 'page_padding');
  const maxContentWidth = themeVal('maxContentWidth', 'max_content_width');
  const blockSpacing = themeVal('blockSpacing', 'block_spacing');

  // Block decorations
  const blockBorderEnabled = pt.blockBorderEnabled ?? pt.block_border_enabled;
  const blockBorderColor = themeVal('blockBorderColor', 'block_border_color');
  const blockShadow = themeVal('blockShadow', 'block_shadow');

  // Background pattern
  const backgroundPattern = themeVal('backgroundPattern', 'background_pattern');

  // P1.5: inject a Google Fonts <link> for the tone-specific heading +
  // body pair. Skip legacy values that already contain a full CSS font
  // stack (commas or quotes) — those are raw CSS and can't be looked up
  // on Google Fonts.
  useEffect(() => {
    const families = [headingFontName, bodyFontName].filter(isGoogleFontName);
    // Deduplicate (minimal tone uses the same font for both slots).
    const uniq = families.filter((f, i) => families.indexOf(f) === i);
    if (uniq.length === 0) { return; }
    const familyParams = uniq
      .map((f) => `family=${encodeURIComponent(f)}:wght@400;600;700`)
      .join('&');
    const href = `https://fonts.googleapis.com/css2?${familyParams}&display=swap`;
    // Avoid re-inserting if the identical link is already in the head.
    const existing = document.head.querySelector(
      `link[data-plugd-fonts="${href}"]`,
    );
    if (existing) { return; }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.setAttribute('data-plugd-fonts', href);
    document.head.appendChild(link);
    return () => {
      link.remove();
    };
  }, [headingFontName, bodyFontName]);

  // SEO: inject meta tags
  useEffect(() => {
    if (!page) return;
    const seoTitle = page.seo_title || page.title;
    const seoDesc = page.seo_description || page.description || '';

    document.title = seoTitle;

    // Helper to set/create meta tags
    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement;
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.content = content;
    };

    if (seoDesc) setMeta('name', 'description', seoDesc);
    setMeta('property', 'og:title', seoTitle);
    if (seoDesc) setMeta('property', 'og:description', seoDesc);
    if (page.og_image_url) setMeta('property', 'og:image', page.og_image_url);
    setMeta('property', 'og:url', window.location.href);
    setMeta('name', 'twitter:card', 'summary_large_image');

    // No-index
    if (page.no_index) {
      setMeta('name', 'robots', 'noindex, nofollow');
    }

    // Favicon
    if (page.favicon_url) {
      let link = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
      link.href = page.favicon_url;
    }

    // Google Analytics
    if (page.google_analytics_id) {
      const gaId = page.google_analytics_id;
      if (!document.querySelector(`script[src*="${gaId}"]`)) {
        const s1 = document.createElement('script');
        s1.async = true;
        s1.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
        s1.setAttribute('data-plugd-ga', 'true');
        document.head.appendChild(s1);
        const s2 = document.createElement('script');
        s2.setAttribute('data-plugd-ga', 'true');
        s2.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${gaId}');`;
        document.head.appendChild(s2);
      }
    }

    // Facebook Pixel
    if (page.facebook_pixel_id) {
      const pixelId = page.facebook_pixel_id;
      if (!document.querySelector(`script[data-plugd-pixel]`)) {
        const sp = document.createElement('script');
        sp.setAttribute('data-plugd-pixel', 'true');
        sp.textContent = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${pixelId}');fbq('track','PageView');`;
        document.head.appendChild(sp);
      }
    }

    return () => {
      document.title = 'PlugD';
      document.querySelectorAll('[data-plugd-ga],[data-plugd-pixel]').forEach(el => el.remove());
    };
  }, [page]);

  // Inject custom CSS
  useEffect(() => {
    if (!customCss) return;
    const style = document.createElement('style');
    style.textContent = customCss;
    style.setAttribute('data-plugd-custom', 'true');
    document.head.appendChild(style);
    return () => { style.remove(); };
  }, [customCss]);

  const localeCtxVal: LocaleCtxVal = { locale, defaultLocale, strings, availableLocales };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
        <div className="w-10 h-10 border-[3px] border-slate-200 border-t-slate-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !page) {
    // P1-5a — distinguish between "never existed" (404) and
    // "intentionally unpublished" (410 with `code: 'page_unpublished'`).
    // The backend now returns 410 when a slug exists but the owner
    // toggled publishing off. We render a friendlier "not live yet"
    // view so visitors don't assume the link is broken.
    const response = getPublicPageResponse(error);
    const isUnpublished =
      response?.status === 410 ||
      response?.data?.code === 'page_unpublished';

    if (isUnpublished) {
      return (
        <LocaleCtx.Provider value={localeCtxVal}>
          <div className="min-h-screen bg-gradient-to-br from-amber-50 to-slate-100 flex items-center justify-center">
            <div className="text-center px-6 max-w-md">
              <div className="text-5xl mb-4" aria-hidden="true">🚧</div>
              <h1 className="text-xl font-bold text-slate-800 mb-1">
                {t(strings, 'page_unpublished_title', 'Not live yet')}
              </h1>
              <p className="text-sm text-slate-500">
                {t(
                  strings,
                  'page_unpublished_desc',
                  "The owner of this page hasn't published it yet. Check back soon or reach out to them directly."
                )}
              </p>
            </div>
          </div>
        </LocaleCtx.Provider>
      );
    }

    return (
      <LocaleCtx.Provider value={localeCtxVal}>
        <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
          <div className="text-center px-6">
            <div className="text-5xl mb-4">🔗</div>
            <h1 className="text-xl font-bold text-slate-800 mb-1">{t(strings, 'page_not_found', 'Page Not Found')}</h1>
            <p className="text-sm text-slate-500">{t(strings, 'page_not_found_desc', "This page doesn't exist or hasn't been published yet.")}</p>
          </div>
        </div>
      </LocaleCtx.Provider>
    );
  }

  // Inline gate background — computed before bgStyle (which is defined below
  // after the password/sensitive gates) to avoid forward-reference issues.
  const gateBg: React.CSSProperties = (() => {
    const s: React.CSSProperties = { color: tc, fontFamily: ff };
    if (gradientStart && gradientEnd) {
      const dir = gradientDirection === 'radial'
        ? 'radial-gradient(circle,'
        : `linear-gradient(${gradientDirection},`;
      s.background = `${dir} ${gradientStart}, ${gradientEnd})`;
    } else {
      s.background = `linear-gradient(160deg, ${bg}, ${bg}ee, ${pc}08)`;
    }
    if (bgImage) {
      s.backgroundImage = `url(${bgImage})`;
      s.backgroundSize = 'cover';
      s.backgroundPosition = 'center';
    }
    return s;
  })();

  // Password gate — API returns metadata-only when password_protected is true
  if (page.password_protected) {
    const handlePasswordSubmit = () => {
      if (!passwordInput.trim()) { return; }
      setPasswordError(false);
      // Set pwd param — the query will re-fetch and if password is correct, page.password_protected will be false
      setSearchParams({ pwd: passwordInput.trim() });
      // If still locked after refetch, show error
      setTimeout(() => { setPasswordError(true); }, 1200);
    };
    return (
      <LocaleCtx.Provider value={localeCtxVal}>
        <div className="min-h-screen flex items-center justify-center" style={gateBg} dir={isRtl ? 'rtl' : undefined}>
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-xl border border-white/80 p-8 max-w-sm w-full mx-4 text-center">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: `${pc}15` }}>
              <Lock className="w-7 h-7" style={{ color: pc }} />
            </div>
            <h1 className="text-xl font-bold mb-1" style={{ color: tc }}>{page.title || t(strings, 'protected_page', 'Protected Page')}</h1>
            <p className="text-sm text-gray-500 mb-6">{t(strings, 'password_protected_desc', 'This page is password protected. Enter the password to continue.')}</p>
            <div className="space-y-3">
              <input
                type="password"
                value={passwordInput}
                onChange={e => { setPasswordInput(e.target.value); setPasswordError(false); }}
                onKeyDown={e => { if (e.key === 'Enter') { handlePasswordSubmit(); } }}
                placeholder={t(strings, 'enter_password', 'Enter password')}
                className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl outline-none focus:ring-2 focus:border-transparent text-center"
                style={{ outlineColor: pc }}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
              {passwordError && pwd && (
                <p className="text-xs text-red-500">{t(strings, 'incorrect_password', 'Incorrect password. Please try again.')}</p>
              )}
              <button
                onClick={handlePasswordSubmit}
                disabled={!passwordInput.trim()}
                className="w-full py-3 text-white font-semibold text-sm rounded-xl shadow-md hover:shadow-lg transition disabled:opacity-40"
                style={{ backgroundColor: pc }}
              >
                {t(strings, 'unlock_page', 'Unlock Page')}
              </button>
            </div>
          </div>
        </div>
      </LocaleCtx.Provider>
    );
  }

  // Age-gate / sensitive content warning
  if (page.sensitive_content && !ageConfirmed) {
    return (
      <LocaleCtx.Provider value={localeCtxVal}>
        <div className="min-h-screen flex items-center justify-center" style={gateBg} dir={isRtl ? 'rtl' : undefined}>
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-xl border border-white/80 p-8 max-w-sm w-full mx-4 text-center">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-amber-50">
              <AlertTriangle className="w-7 h-7 text-amber-500" />
            </div>
            <h1 className="text-xl font-bold mb-1" style={{ color: tc }}>{t(strings, 'age_restricted', 'Age Restricted Content')}</h1>
            <p className="text-sm text-gray-500 mb-6">
              {t(strings, 'age_restricted_desc', 'This page contains content intended for adults only. By proceeding, you confirm that you are at least 18 years old.')}
            </p>
            <div className="space-y-3">
              <button
                onClick={() => setAgeConfirmed(true)}
                className="w-full py-3 text-white font-semibold text-sm rounded-xl shadow-md hover:shadow-lg transition"
                style={{ backgroundColor: pc }}
              >
                {t(strings, 'age_confirm', 'I am 18 or older — Continue')}
              </button>
              <button
                onClick={() => window.history.back()}
                className="w-full py-3 text-gray-600 font-medium text-sm rounded-xl border border-gray-200 hover:bg-gray-50 transition"
              >
                {t(strings, 'go_back', 'Go Back')}
              </button>
            </div>
          </div>
        </div>
      </LocaleCtx.Provider>
    );
  }

  // P3-1: Defense-in-depth. The backend route `get_public_page` already
  // filters scheduled blocks server-side, but we also re-evaluate here
  // so cached/prerendered pages don't reveal content past its end_at or
  // during days/times it shouldn't be visible. `isScheduleActive`
  // fail-opens on malformed data so broken schedules never hide content.
  const blocks = (page.blocks || [])
    .filter(b => b.is_visible !== false && isScheduleActive(b.visibility_schedule))
    .sort((a, b) => a.order - b.order);
  const hasAvatar = blocks.some(b => b.block_type === 'avatar');
  const htmlBlockCount = blocks.filter(b => b.block_type === 'html').length;
  const usesEditorialHtmlShell =
    blocks.length > 0 &&
    !hasAvatar &&
    blocks[0]?.block_type === 'html' &&
    htmlBlockCount >= Math.max(1, blocks.length - 1);
  const pageUrl = window.location.href;
  const isPreviewPage = Boolean(page?.is_preview);

  const renderBlock = (b: Block) => {
    const s = b.settings;
    switch (b.block_type) {
      case 'avatar': return <AvatarBlock key={b.id} s={s} pc={pc} />;
      case 'heading': return <HeadingBlock key={b.id} s={s} />;
      case 'text': return <TextBlock key={b.id} s={s} />;
      case 'button': return <ButtonBlock key={b.id} s={s} pc={pc} r={r} />;
      case 'icon_text': return <IconTextBlock key={b.id} s={s} />;
      case 'social_networks': return <SocialBlock key={b.id} s={s} />;
      case 'messengers': return <MessengersBlock key={b.id} s={s} />;
      case 'divider': return <DividerBlock key={b.id} s={s} />;
      case 'banner': return <BannerBlock key={b.id} s={s} r={r} />;
      case 'gallery': return <GalleryBlock key={b.id} s={s} r={r} />;
      // P2-1 image carousel
      case 'carousel': return <CarouselBlock key={b.id} s={s} r={r} />;
      case 'video': return <VideoBlock key={b.id} s={s} r={r} />;
      // P2-2 music embed
      case 'music': return <MusicBlock key={b.id} s={s} r={r} />;
      // P2-3 FAQ accordion (distinct from legacy 'questions')
      case 'faq': return <FaqBlock key={b.id} s={s} r={r} />;
      // P2-4 product card
      case 'product': return <ProductBlock key={b.id} s={s} pc={pc} r={r} />;
      case 'qr_code': return <QrCodeBlock key={b.id} s={s} pageUrl={pageUrl} />;
      case 'working_hours': return <WorkingHoursBlock key={b.id} s={s} r={r} />;
      case 'timer': return <TimerBlock key={b.id} s={s} pc={pc} />;
      case 'map': return <MapBlock key={b.id} s={s} r={r} />;
      case 'questions': return <QuestionsBlock key={b.id} s={s} r={r} />;
      case 'form': return <FormBlock key={b.id} s={s} blockId={b.id} pc={pc} r={r} pageSlug={slug} isPreview={isPreviewPage} />;
      case 'payment': return <PaymentBlock key={b.id} s={{ ...s, id: b.id }} pc={pc} r={r} pageSlug={slug} isPreview={isPreviewPage} />;
      // P3-2 tip jar (Stripe Connect donation widget)
      case 'tip_jar': return <TipJarBlock key={b.id} s={{ ...s, id: b.id }} pc={pc} r={r} pageSlug={slug} isPreview={isPreviewPage} />;
      case 'html': return <HtmlBlock key={b.id} s={s} />;
      case 'columns': return <ColumnsBlock key={b.id} s={s} r={r} />;
      // P3-3 custom modular grid
      case 'grid': return <GridBlock key={b.id} s={s} pc={pc} r={r} />;
      case 'language_switcher': return <LanguageSwitcherBlock key={b.id} s={s} pc={pc} />;
      default: return null;
    }
  };

  // Build background style — support gradients, solid color, and image
  const bgStyle: React.CSSProperties = (() => {
    const s: React.CSSProperties = { color: tc, fontFamily: ff };
    if (gradientStart && gradientEnd) {
      const dir = gradientDirection === 'radial'
        ? 'radial-gradient(circle,'
        : `linear-gradient(${gradientDirection},`;
      s.background = `${dir} ${gradientStart}, ${gradientEnd})`;
    } else {
      s.background = `linear-gradient(160deg, ${bg}, ${bg}ee, ${pc}08)`;
    }
    if (bgImage) {
      s.backgroundImage = `url(${bgImage})`;
      s.backgroundSize = 'cover';
      s.backgroundPosition = 'center';
    }
    return s;
  })();

  // Block-level styles (spacing, border, shadow)
  const blockItemStyle: React.CSSProperties = (() => {
    const s: React.CSSProperties = {};
    if (blockSpacing) { s.marginBottom = blockSpacing; }
    if (blockBorderEnabled && blockBorderColor) { s.border = `1px solid ${blockBorderColor}`; }
    if (blockShadow && blockShadow !== 'none') {
      const shadowMap: Record<string, string> = {
        small: '0 1px 3px rgba(0,0,0,0.08)',
        medium: '0 4px 12px rgba(0,0,0,0.1)',
        large: '0 8px 24px rgba(0,0,0,0.12)',
      };
      s.boxShadow = shadowMap[blockShadow] || 'none';
    }
    return s;
  })();
  const hasBlockItemStyle = Object.keys(blockItemStyle).length > 0;

  // Container-level styles (padding, max-width)
  const contentWrapStyle: React.CSSProperties = (() => {
    const s: React.CSSProperties = {};
    if (pagePadding) { s.paddingLeft = pagePadding; s.paddingRight = pagePadding; }
    if (usesEditorialHtmlShell) {
      s.width = '100%';
      s.maxWidth = '320px';
      s.marginLeft = 'auto';
      s.marginRight = 'auto';
    } else if (maxContentWidth) {
      s.maxWidth = maxContentWidth;
      s.marginLeft = 'auto';
      s.marginRight = 'auto';
    }
    return s;
  })();

  // SVG background patterns — mirrors PhonePreview implementation
  const patternOverlay = (() => {
    if (!backgroundPattern) { return null; }
    const patternSVGs: Record<string, string> = {
      stripes: "data:image/svg+xml,%3Csvg width='40' height='40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 40L40 0H20L0 20zM40 40V20L20 40z' fill='%23000' fill-opacity='0.04'/%3E%3C/svg%3E",
      dots: "data:image/svg+xml,%3Csvg width='20' height='20' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='2' cy='2' r='1.5' fill='%23000' fill-opacity='0.06'/%3E%3C/svg%3E",
      grid: "data:image/svg+xml,%3Csvg width='20' height='20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 0H0v20' fill='none' stroke='%23000' stroke-opacity='0.04'/%3E%3C/svg%3E",
      chevron: "data:image/svg+xml,%3Csvg width='40' height='20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 10l20-10 20 10' fill='none' stroke='%23000' stroke-opacity='0.05'/%3E%3C/svg%3E",
      waves: "data:image/svg+xml,%3Csvg width='40' height='20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 10c10-10 30 10 40 0' fill='none' stroke='%23000' stroke-opacity='0.05'/%3E%3C/svg%3E",
      diamond: "data:image/svg+xml,%3Csvg width='20' height='20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10 0l10 10-10 10L0 10z' fill='none' stroke='%23000' stroke-opacity='0.04'/%3E%3C/svg%3E",
      hexagon: "data:image/svg+xml,%3Csvg width='30' height='26' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M15 0l15 7.5v11L15 26 0 18.5v-11z' fill='none' stroke='%23000' stroke-opacity='0.04'/%3E%3C/svg%3E",
      triangles: "data:image/svg+xml,%3Csvg width='20' height='20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10 0l10 20H0z' fill='none' stroke='%23000' stroke-opacity='0.05'/%3E%3C/svg%3E",
      circles: "data:image/svg+xml,%3Csvg width='20' height='20' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='10' cy='10' r='8' fill='none' stroke='%23000' stroke-opacity='0.04'/%3E%3C/svg%3E",
      squares: "data:image/svg+xml,%3Csvg width='20' height='20' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='2' y='2' width='16' height='16' fill='none' stroke='%23000' stroke-opacity='0.04'/%3E%3C/svg%3E",
      zigzag: "data:image/svg+xml,%3Csvg width='40' height='10' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 5l10-5 10 5 10-5 10 5' fill='none' stroke='%23000' stroke-opacity='0.05'/%3E%3C/svg%3E",
      cross: "data:image/svg+xml,%3Csvg width='20' height='20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10 0v20M0 10h20' stroke='%23000' stroke-opacity='0.04'/%3E%3C/svg%3E",
    };
    const url = patternSVGs[backgroundPattern];
    if (!url) { return null; }
    return <div className="absolute inset-0 pointer-events-none z-0" style={{ backgroundImage: `url("${url}")`, backgroundRepeat: 'repeat' }} />;
  })();

  // Wrap each block with analytics attributes and optional per-block
  // styling (border, shadow, spacing).
  const wrapBlock = (key: string, inner: React.ReactNode) => {
    const block = blocks.find((b) => b.id === key);
    const tracked = (
      <div
        key={key}
        data-page-block-id={key}
        data-page-block-type={block?.block_type}
        onClickCapture={handleBlockClickCapture}
      >
        {inner}
      </div>
    );
    if (hasBlockItemStyle) {
      return <div key={key} style={blockItemStyle}>{tracked}</div>;
    }
    return tracked;
  };

  return (
    <LocaleCtx.Provider value={localeCtxVal}>
      <PageTrackCtx.Provider value={trackPageEvent}>
        <div className="min-h-screen relative" style={bgStyle} dir={isRtl ? 'rtl' : undefined}>
          {/* Link color + heading font (P1.5 tone-specific pairing) */}
          <style>{`a { color: ${lc}; } a:hover { opacity: 0.8; } h1, h2, h3, h4, h5, h6 { font-family: ${headingFf}; }`}</style>
          {/* Background pattern overlay */}
          {patternOverlay}
          {/* Decorative top accent */}
          <div className="h-40 w-full absolute top-0 left-0 right-0 z-0" style={{ background: `linear-gradient(180deg, ${pc}15, transparent)` }} />

          <div className="relative max-w-[440px] mx-auto min-h-screen px-5 pt-12 pb-8 z-[1]" style={contentWrapStyle}>
            {usesEditorialHtmlShell ? (
              <div className="relative overflow-hidden sm:rounded-[2rem] sm:border-[6px] sm:border-slate-900/90 sm:bg-slate-900/90 sm:p-[6px] sm:shadow-[0_24px_70px_rgba(15,23,42,0.26)]">
                <div className="hidden sm:block absolute left-1/2 top-[6px] z-20 h-5 w-20 -translate-x-1/2 rounded-b-xl bg-slate-900/90" />
                <div className="overflow-hidden sm:h-[560px] sm:overflow-y-auto sm:rounded-[1.55rem] scrollbar-hide">
                  {page.title && (
                    <div className="hidden sm:block border-b border-white/10 px-4 py-2.5 text-center">
                      <h1 className="text-[12px] font-bold" style={{ color: tc, fontFamily: headingFf }}>
                        {page.title}
                      </h1>
                    </div>
                  )}
                  <div className={blockSpacing ? '' : 'space-y-1'}>
                    {blocks.map(b => wrapBlock(b.id, renderBlock(b)))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white/70 backdrop-blur-xl rounded-3xl shadow-xl border border-white/80 p-6 pb-8" style={{ boxShadow: `0 8px 40px ${pc}10, 0 2px 8px rgba(0,0,0,0.06)` }}>
                {!hasAvatar && page.title && (
                  <div className="flex flex-col items-center gap-3 pb-4">
                    <div className="w-24 h-24 rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-xl" style={{ background: `linear-gradient(135deg, ${pc}, ${pc}88)`, boxShadow: `0 8px 24px ${pc}40` }}>
                      {page.title.charAt(0).toUpperCase()}
                    </div>
                    <h1 className="text-2xl font-extrabold tracking-tight">{page.title}</h1>
                    {page.description && <p className="text-sm opacity-40 text-center max-w-[260px]">{page.description}</p>}
                  </div>
                )}

                <div className={blockSpacing ? '' : 'space-y-1'}>
                  {blocks.map(b => wrapBlock(b.id, renderBlock(b)))}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="pt-8 pb-4 text-center">
              <a href="/" className="inline-flex items-center gap-2 opacity-25 hover:opacity-40 transition text-xs font-medium">
                <span className="w-5 h-5 rounded-md flex items-center justify-center text-white text-[9px] font-bold shadow-sm" style={{ backgroundColor: pc }}>P</span>
                {t(strings, 'made_with', 'Made with')} PlugD
              </a>
            </div>
          </div>
        </div>
      </PageTrackCtx.Provider>
    </LocaleCtx.Provider>
  );
};

export default PublicPage;
